import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { LocationType } from '../../types';

interface LocationFormData {
  name: string;
  type: LocationType;
  code?: string;
  description?: string;
  address?: string;
  city?: string;
  wilaya?: string;
  zipCode?: string;
  country: string;
  phone?: string;
  email?: string;
  currency?: string;
  organizationId: string;
  canSellToCustomers: boolean;
  canFulfillOnlineOrders: boolean;
  canReceiveTransfers: boolean;
  canSendTransfers: boolean;
  isActive: boolean;
}

export const LocationCreate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<LocationFormData>({
    defaultValues: {
      type: 'store',
      country: 'Algeria',
      currency: 'DZD',
      canSellToCustomers: true,
      canFulfillOnlineOrders: false,
      canReceiveTransfers: true,
      canSendTransfers: true,
      isActive: true,
    },
  });

  const watchType = watch('type');
  const canSellToCustomers = watch('canSellToCustomers');
  const canFulfillOnlineOrders = watch('canFulfillOnlineOrders');
  const canReceiveTransfers = watch('canReceiveTransfers');
  const canSendTransfers = watch('canSendTransfers');
  const isActive = watch('isActive');

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/organizations');
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: LocationFormData) => {
      // Use the appropriate endpoint based on type
      const endpoint = data.type === 'store' ? '/locations/store' : '/locations/warehouse';
      return axiosInstance.post(endpoint, data);
    },
    onSuccess: () => {
      showMessage('Location created successfully', 'success');
      navigate('/locations');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create location', 'error');
    },
  });

  const onSubmit = (data: LocationFormData) => {
    createMutation.mutate(data);
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Create Location
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('name', { required: 'Location name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  label="Location Name"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  {...register('type', { required: 'Type is required' })}
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  fullWidth
                  select
                  label="Type"
                  value={watchType}
                  onChange={(e) => setValue('type', e.target.value as LocationType)}
                >
                  <MenuItem value="store">Store</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  {...register('code')}
                  fullWidth
                  label="Code"
                  placeholder="e.g., STORE-001"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('organizationId', { required: 'Organization is required' })}
                  error={!!errors.organizationId}
                  helperText={errors.organizationId?.message}
                  fullWidth
                  select
                  label="Organization"
                  defaultValue=""
                >
                  <MenuItem value="">Select Organization</MenuItem>
                  {organizations?.map((org: any) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Address Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('address')}
                  fullWidth
                  multiline
                  rows={2}
                  label="Address"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('city')}
                  fullWidth
                  label="City"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('wilaya')}
                  fullWidth
                  label="Wilaya"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('zipCode')}
                  fullWidth
                  label="Zip Code"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('country')}
                  fullWidth
                  label="Country"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('currency')}
                  fullWidth
                  select
                  label="Currency"
                  defaultValue="DZD"
                >
                  <MenuItem value="DZD">DZD - Algerian Dinar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                </TextField>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Contact
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('phone')}
                  fullWidth
                  label="Phone"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('email')}
                  fullWidth
                  type="email"
                  label="Email"
                />
              </Grid>

              {/* Capabilities */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Capabilities
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canSellToCustomers}
                      onChange={(e) => setValue('canSellToCustomers', e.target.checked)}
                    />
                  }
                  label="Can sell to customers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canFulfillOnlineOrders}
                      onChange={(e) => setValue('canFulfillOnlineOrders', e.target.checked)}
                    />
                  }
                  label="Can fulfill online orders"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canReceiveTransfers}
                      onChange={(e) => setValue('canReceiveTransfers', e.target.checked)}
                    />
                  }
                  label="Can receive transfers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canSendTransfers}
                      onChange={(e) => setValue('canSendTransfers', e.target.checked)}
                    />
                  }
                  label="Can send transfers"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setValue('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => navigate('/locations')}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
